/**
 * Remembers where the user was actually trying to go when an unauthenticated
 * visit bounced them to /login, so signing in lands them there instead of on
 * the generic home route.
 *
 * Deliberately URL-agnostic: nothing here knows about /knowledge-base or any
 * other page. Any protected route — present or future, with whatever query
 * string or hash it carries — is preserved by the same code path. The
 * motivating case was a superadmin invite mailing an admin a link to
 * /knowledge-base?setup=review, whose `?setup=review` was the entire point of
 * the mail and was silently dropped along with the path.
 *
 * sessionStorage rather than React Router location state: the sign-in path
 * can involve a full document load (Firebase persistence rehydrating, or the
 * user simply reloading the login page), which router state does not reliably
 * survive, and the value is read from three unrelated places that would
 * otherwise each have to thread it through.
 */

const STORAGE_KEY = 'apexia.returnTo';

/** Auth pages are never a useful destination to return *to* — sending the
 *  user back to /login after they just logged in is a redirect loop. */
const NON_DESTINATIONS = ['/login', '/signup'];

/**
 * Only same-origin, absolute-path destinations are ever stored. Rejecting
 * protocol-relative (`//evil.com`) and absolute URLs keeps a crafted link
 * from turning the login redirect into an open redirect.
 */
const isSafeInternalPath = (path: string): boolean => {
  if (!path.startsWith('/') || path.startsWith('//')) return false;
  const [pathname] = path.split(/[?#]/);
  return !NON_DESTINATIONS.includes(pathname);
};

type PartialLocation = { pathname: string; search?: string; hash?: string };

/** Records the attempted URL — path, query and hash — as the post-login
 *  destination. A no-op for auth pages and anything not a safe internal path. */
export const rememberReturnTo = (location: PartialLocation): void => {
  const target = `${location.pathname}${location.search || ''}${location.hash || ''}`;
  if (!isSafeInternalPath(target)) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, target);
  } catch {
    // Storage disabled or full — deep-link restore is a convenience, so
    // fall back silently to the caller's default home route.
  }
};

/** The stored destination, or null. Does not clear it: the destination is
 *  read from more than one place during a single sign-in (the login page's
 *  own redirect effect and the unprotected-route guard can both resolve it),
 *  and those must agree rather than race over who consumed it. */
export const peekReturnTo = (): string | null => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored && isSafeInternalPath(stored) ? stored : null;
  } catch {
    return null;
  }
};

/** Drops the stored destination. Called once the user is authenticated and
 *  rendering a protected route — the point at which it has served its purpose
 *  and would otherwise misdirect the *next* sign-in in this tab. */
export const clearReturnTo = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do — a value we cannot remove is one we could not have read.
  }
};
